<!--
     <DynamicForm
       :fields="my_fields"
       @error="alert"
     />

     Parameters:
       - fields
       - return_object: Return JS object instead of FormData

     Public methods
       validate() → Validates the data and returns true if data is valid.
       clear()    → Clears all inputs
       get_data() → Returns a filled FormData() instance,
                    NOTE: Fields returning /undefined/ value are ignored.
                    If you need an object, use return_object parameter

     fields = [ // rows
         [ // cols
             {
               component: "TextInput",
               cols: 4, // Col size int, [1 ↔ 12].
               hidden: false // Hides the field
               id: "Unique component name for the form",
               ... // Extra propertys will be forwarded to the component
             }
         ]
     ]

     NOTE: For hidden fields, their default value is used
-->

<template>
  <v-form
    v-model="form"
  >
    <v-row
      dense
      v-for="(row,row_index) in current_fields"
      :key="row.map((f) => f.id).join('|')"
      class="dynamicform-row"
    >
      <v-col
        v-for="(field,col_index) in row"
        :key="field.id"
        :cols="forced_cols || field.cols"
        class="dynamicform-col"
        :class="field.hidden ? 'dynamicform-hidden-col' : ''"
      >
        <CustomInput
          :form_values="values"
          :field="field"
          :current_unit="unit"
          @update="(value) => {update_values(field,value,row_index,col_index)}"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-alert dense type="error" v-if="max_file_size_reached">
          {{ this.$t('max_file_size_reached') }}
        </v-alert>
      </v-col>
    </v-row>
  </v-form>
</template>


<style lang="scss">
 .dynamicform-row {
   flex-direction: row;
 }

 @media (orientation: portrait) {
   .dynamicform-row {
     display: flex;
     flex-direction: column;
   }

   .dynamicform-col {
     display: block;
     width: 100%;
     max-width: 100% !important;
   }
 }

 .dynamicform-hidden-col {
   /* Render the component but hide it from the UI
    * We set the scale to a very small number to prevent blank spaces
    */
   visibility: hidden;
   height: 1px !important;
   scale: 0.01 !important;
 }
</style>


<script>
import CustomInput from '@/components/inputs/CustomInput.vue';
import { is_dictionary } from '@/utils/logic.js';
import { mapMutations } from 'vuex';
import { WarningAlert } from '@/utils/Alerts';


export default {
  components: {
    CustomInput,
  },

  name: 'DynamicForm',

  props: {
    fields: {required: true},
    unit: {},
    return_object: {type: Boolean, 'default': false},
  },

  data: () => ({
    form: null,
    values: {},
    current_fields: {},

    MAX_MODAL_FILE_SIZE: 19000000, //19Mb
  }),

  computed: {
    fieldlist() {
      // Returns a list with all fields [field1, field2...]
      return this.fields.flat();
    },

    max_file_size_reached() {
      let initialValue = 0;
      const file_fields = this.fieldlist.filter(it => it.component == "FileInput").map(it => (this.values[it.id] || {}).size);
      for(let b of file_fields) {
        initialValue += (b || 0);
      }
      return initialValue >= this.MAX_MODAL_FILE_SIZE;
    },

    is_valid() {
      return this.form && !this.max_file_size_reached;
    },
    forced_cols() {
      return screen.width <= 600 ? 12 : 0;
    },
  },

  beforeMount() {
    for (const field of this.fieldlist) {
      this.values[field.id] = field.value;
    }
    //Copy array
    this.current_fields = (this.fields || []).slice();
  },

  methods: {
    ...mapMutations(['SET_MESSAGE']),
    validate() {
      // Validates all fields and returns if all of them are valid
      let v = !this.fieldlist.some(
        (f) => this.$refs[f.id].validate && !this.$refs[f.id].validate(),
      )
      return v
    },

    get_data() {
      // Returns a FormData() with the data of all fields
      // If prop return_object is set, returns an object {} instead
      const retdata = {};
      let value = null;
      for (const field of this.fieldlist) {
        value = null;
        if (field.hidden) {
          value = field.value;
        } else {
          value = this.values[field.id];
        }
        if (value !== undefined && value !== null) {
          if (!this.return_object && (Array.isArray(value) || is_dictionary(value))) {
            // Convert arrays and dictionaries to a JSON string for the backend
            // otherwise FormData will generate the string "[object Object]"
            value = JSON.stringify(value);
          }
          retdata[field.id] = value;
        }
      }

      if (this.return_object) {
        return retdata;
      }

      // Generate FromData
      const fdata = new FormData();
      for (const [key, value] of Object.entries(retdata)) {
        fdata.append(key, value);
      }
      return fdata;
    },

    update_values(field,value,row,col) {
      this.current_fields[row][col].value = value;
      this.values = {...this.values,[field.id]: value};
    },

    clear() {
      // Clears all inputs value
      // TODO: FIXME: don't work into some inputs ex. FileInput or Select
      for (let row of this.current_fields) {
        for (let field of row) {
          if (!field.readonly && !field.hidden && (field.value!= null)) {
            this.values[field.id] = null;
            field = Object.assign(field,{value : null});
          }
        }
      }
    },
  },

  watch: {
    is_valid(val) {
      this.$emit('ready', val);
    },
  },
}
</script>
